import { useEffect, useRef, useState, type FormEvent } from "react";
import { Spinner } from "@/components/ui/spinner";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useSignIn } from "@clerk/react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AuthContent,
  AuthErrorAlert,
  AuthLoading,
  AuthShell,
  CodeInput,
  FieldMessage,
  OAuthButtons,
  OrDivider,
  TextButton,
} from "@/components/auth/AuthLayout";
import {
  APP_HOME,
  OAUTH_STALL_MS,
  SSO_CALLBACK_PATH,
  navigateToApp,
  useResendCooldown,
  type AuthOAuthStrategy,
} from "@/components/auth/auth-utils";

const HEADLINE = "Amy kept your creator conversations moving while you were away.";

/**
 * Sign in with an emailed one-time code or Google / Microsoft — the factors
 * the Clerk instance offers — built on Clerk's signal-based useSignIn.
 * An invitation for an existing user (?__clerk_ticket=…) signs straight in.
 */
export default function LogIn() {
  const { signIn, errors, fetchStatus } = useSignIn();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const ticket = searchParams.get("__clerk_ticket");

  const [step, setStep] = useState<"email" | "code">("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [oauthPending, setOauthPending] = useState<AuthOAuthStrategy | null>(null);
  const [acceptingTicket, setAcceptingTicket] = useState(!!ticket);
  const [stepError, setStepError] = useState<string | null>(null);
  const cooldown = useResendCooldown();
  const ticketStarted = useRef(false);
  const busy = fetchStatus === "fetching";

  const finishIfComplete = async () => {
    if (signIn.status === "complete") {
      await signIn.finalize({ navigate: navigateToApp(navigate) });
      return true;
    }
    return false;
  };

  // clerk-js's sso() only starts a new sign-in when the client has none (or
  // the current one is already OAuth). A leftover sign-in, e.g. an email
  // attempt from an earlier visit, makes it resolve without redirecting, so
  // start this page from a clean one. reset() swaps in a new sign-in object,
  // which the hook hands back on the next render.
  useEffect(() => {
    if (!ticket && signIn.id && signIn.status !== "complete") void signIn.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Once only: a ticket is single-use, and StrictMode runs effects twice.
  useEffect(() => {
    if (!ticket || ticketStarted.current) return;
    ticketStarted.current = true;
    void (async () => {
      const { error } = await signIn.ticket({ ticket });
      if (!error && (await finishIfComplete())) return;
      setAcceptingTicket(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket]);

  const handleSendCode = async (e: FormEvent) => {
    e.preventDefault();
    setStepError(null);
    const { error } = await signIn.create({ identifier: email.trim() });
    if (error) return;
    const { error: sendError } = await signIn.emailCode.sendCode();
    if (sendError) {
      // Don't leave the half-started sign-in behind for the OAuth buttons.
      void signIn.reset();
      return;
    }
    setCode("");
    setStep("code");
    cooldown.start();
  };

  const verify = async (value: string) => {
    setStepError(null);
    const { error } = await signIn.emailCode.verifyCode({ code: value });
    if (error) return;
    if (!(await finishIfComplete())) {
      setStepError(
        "This account needs another verification step we can't complete here. Contact support.",
      );
    }
  };

  const handleResend = async () => {
    const { error } = await signIn.emailCode.sendCode();
    if (error) return;
    cooldown.start();
    toast.success(`New code sent to ${email.trim()}`);
  };

  const handleBack = () => {
    setStep("email");
    setCode("");
    setStepError(null);
    void signIn.reset();
  };

  const handleOAuth = async (strategy: AuthOAuthStrategy) => {
    setStepError(null);
    setOauthPending(strategy);
    const { error } = await signIn.sso({
      strategy,
      redirectUrl: APP_HOME,
      redirectCallbackUrl: SSO_CALLBACK_PATH,
    });
    if (error) {
      setOauthPending(null);
      return;
    }
    // On success the browser is on its way to the provider. If it still
    // hasn't left after a few seconds, let the button be pressed again.
    window.setTimeout(() => setOauthPending(null), OAUTH_STALL_MS);
  };

  if (acceptingTicket) {
    return (
      <AuthShell headline={HEADLINE}>
        <AuthLoading title="Joining your team" description="Signing you in with your invitation…" />
      </AuthShell>
    );
  }

  const footer = (
    <>
      Don't have an account?{" "}
      <Link to="/signup" className="font-medium text-primary hover:underline">
        Sign up
      </Link>
    </>
  );

  if (step === "code") {
    return (
      <AuthShell headline={HEADLINE}>
        <AuthContent
          title="Check your email"
          description={
            <>
              Enter the 6-digit code we sent to{" "}
              <span className="font-medium text-foreground">{email.trim()}</span>
            </>
          }
          footer={footer}
        >
          <AuthErrorAlert errors={errors.global} message={stepError} />
          <form
            className="flex flex-col gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              void verify(code);
            }}
          >
            <div className="flex flex-col gap-2">
              <Label htmlFor="sign_in_code" className="sr-only">
                Verification code
              </Label>
              <CodeInput
                id="sign_in_code"
                autoFocus
                value={code}
                disabled={busy}
                invalid={!!errors.fields.code}
                onChange={setCode}
                onComplete={(value) => void verify(value)}
              />
              <FieldMessage error={errors.fields.code} />
            </div>
            <Button type="submit" className="h-10 text-sm" disabled={busy || code.length < 6}>
              {busy && <Spinner />}
              Continue
            </Button>
            <div className="flex items-center justify-between">
              <TextButton onClick={handleBack} disabled={busy}>
                Use a different email
              </TextButton>
              <TextButton
                onClick={() => void handleResend()}
                disabled={busy || cooldown.left > 0}
              >
                {cooldown.left > 0 ? `Resend code in ${cooldown.left}s` : "Resend code"}
              </TextButton>
            </div>
          </form>
        </AuthContent>
      </AuthShell>
    );
  }

  const identifierError = errors.fields.identifier;

  return (
    <AuthShell headline={HEADLINE}>
      <AuthContent
        title="Sign in to Uplodio"
        description="Welcome back. Continue with Google, Microsoft or your work email."
        footer={footer}
      >
        <AuthErrorAlert errors={errors.global} message={stepError} />
        <OAuthButtons pending={oauthPending} disabled={busy} onSelect={(s) => void handleOAuth(s)} />
        <OrDivider />
        <form className="flex flex-col gap-4" onSubmit={(e) => void handleSendCode(e)}>
          <div className="flex flex-col gap-2">
            <Label htmlFor="sign_in_email">Email address</Label>
            <Input
              id="sign_in_email"
              className="h-10"
              type="email"
              autoComplete="email"
              autoFocus
              required
              placeholder="name@company.com"
              value={email}
              disabled={busy}
              aria-invalid={!!identifierError}
              onChange={(e) => setEmail(e.target.value)}
            />
            <FieldMessage error={identifierError} />
            {identifierError?.code === "form_identifier_not_found" && (
              <p className="text-xs text-muted-foreground">
                New here?{" "}
                <Link to="/signup" className="font-medium text-primary hover:underline">
                  Create an account
                </Link>
              </p>
            )}
          </div>
          <Button
            type="submit"
            className="h-10 text-sm"
            disabled={busy || !email.trim() || oauthPending !== null}
          >
            {busy && <Spinner />}
            Continue with email
          </Button>
        </form>
      </AuthContent>
    </AuthShell>
  );
}
